import type {
  DynamicFieldValue,
  DynamicFieldValues,
  EmrCodeDynamicField,
  EmrCodeFieldType,
} from 'features/Procedure/state/procedure-api.type'

const OPTION_FIELD_TYPES: EmrCodeFieldType[] = [
  'dropdown',
  'multiselect',
  'checkboxgroup',
  'radiogroup',
]

export type DynamicFieldsDto = Record<string, string | number | string[] | null>

export const isRenderableField = (field: EmrCodeDynamicField): boolean =>
  !OPTION_FIELD_TYPES.includes(field.type) || (field.bindings?.length ?? 0) > 0

export const isDynamicFieldValueEmpty = (value: DynamicFieldValue): boolean => {
  if (value === undefined || value === null) {
    return true
  }
  if (typeof value === 'string') {
    return value.trim() === ''
  }
  if (Array.isArray(value)) {
    return value.length === 0
  }
  return false
}

export const getMissingRequiredDynamicFields = (
  fields: EmrCodeDynamicField[] | undefined,
  values: DynamicFieldValues | undefined,
): EmrCodeDynamicField[] =>
  (fields ?? [])
    .filter(isRenderableField)
    .filter((field) => field.required && isDynamicFieldValueEmpty(values?.[field.name]))

export const buildDynamicFieldsDto = (
  fields: EmrCodeDynamicField[] | undefined,
  values: DynamicFieldValues | undefined,
): DynamicFieldsDto =>
  Object.fromEntries(
    (fields ?? [])
      .filter(isRenderableField)
      .map((field) => [field.name, values?.[field.name] ?? null]),
  )

const MULTI_VALUE_FIELD_TYPES: EmrCodeFieldType[] = ['multiselect', 'checkboxgroup']

export const tryParseJson = (value: unknown): unknown => {
  if (typeof value !== 'string') {
    return value
  }
  try {
    return JSON.parse(value) as unknown
  } catch {
    return value
  }
}

export const toIntegerValue = (rawValue: unknown): number | undefined => {
  const numericValue = typeof rawValue === 'number' ? rawValue : Number(rawValue)
  return Number.isFinite(numericValue) ? Math.trunc(numericValue) : undefined
}

const normalizeSourceValue = (field: EmrCodeDynamicField, rawValue: unknown): DynamicFieldValue => {
  if (MULTI_VALUE_FIELD_TYPES.includes(field.type)) {
    const parsedValue = tryParseJson(rawValue)
    return Array.isArray(parsedValue) ? parsedValue.map(String) : [String(parsedValue)]
  }
  if (field.type === 'integer') {
    return toIntegerValue(rawValue)
  }
  if (typeof rawValue === 'number') {
    return rawValue
  }
  return String(rawValue)
}

export const extractDynamicFieldValues = (
  fields: EmrCodeDynamicField[] | undefined,
  source: Record<string, unknown>,
): DynamicFieldValues =>
  Object.fromEntries(
    (fields ?? [])
      .filter(isRenderableField)
      .map((field) => ({ field, rawValue: source[field.name] }))
      .filter(({ rawValue }) => rawValue !== undefined && rawValue !== null && rawValue !== '')
      .map(({ field, rawValue }) => [field.name, normalizeSourceValue(field, rawValue)])
      .filter(([, value]) => value !== undefined),
  )

export const getNonIntegerDynamicFields = (
  fields: EmrCodeDynamicField[] | undefined,
  values: DynamicFieldValues | undefined,
): EmrCodeDynamicField[] =>
  (fields ?? [])
    .filter(isRenderableField)
    .filter((field) => field.type === 'integer')
    .filter((field) => {
      const value = values?.[field.name]
      if (isDynamicFieldValueEmpty(value)) {
        return false
      }
      return !Number.isInteger(typeof value === 'number' ? value : Number(value))
    })

export const STRING_FIELD_MAX_LENGTH = 255

export const getTooLongStringDynamicFields = (
  fields: EmrCodeDynamicField[] | undefined,
  values: DynamicFieldValues | undefined,
): EmrCodeDynamicField[] =>
  (fields ?? [])
    .filter(isRenderableField)
    .filter((field) => field.type === 'string')
    .filter((field) => {
      const value = values?.[field.name]
      return typeof value === 'string' && value.length > STRING_FIELD_MAX_LENGTH
    })
